import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { ApiError, authApi } from '../api';
import { finalizeTokens } from '../session-helpers';
import { usePreAuthStore } from '../pre-auth-store';
import { AuthShell } from '../components/AuthShell';

const loginSchema = z.object({
  email: z.string().email('Enter a valid email'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
  rememberMe: z.boolean().default(true),
});

type LoginFormValues = z.infer<typeof loginSchema>;

export function LoginPage() {
  const navigate = useNavigate();
  const setPreAuth = usePreAuthStore((s) => s.set);
  const [formError, setFormError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '', rememberMe: true },
  });

  const onSubmit = async (values: LoginFormValues) => {
    setFormError(null);
    try {
      const res = await authApi.login(values.email, values.password, values.rememberMe);
      if (res.next === 'dashboard') {
        await finalizeTokens(res.tokens);
        navigate('/command-center', { replace: true });
      } else {
        setPreAuth({
          preAuthToken: res.pre_auth_token,
          memberships: res.memberships,
        });
        navigate('/select-tenant');
      }
    } catch (err) {
      if (err instanceof ApiError) {
        setFormError(err.problem.detail || err.problem.title || 'Login failed');
      } else {
        setFormError('Unexpected error — please try again');
      }
    }
  };

  return (
    <AuthShell mode="signin">
      <h1 className="text-2xl font-semibold tracking-tight">Welcome back</h1>
      <p className="mt-1.5 text-sm text-muted-foreground">Sign in to your Command Center.</p>

      <form className="mt-8 space-y-5" onSubmit={handleSubmit(onSubmit)} noValidate>
        <div>
          <label htmlFor="email" className="block text-sm font-medium">
            Email
          </label>
          <input
            id="email"
            type="email"
            autoComplete="email"
            className="auth-input mt-1.5"
            {...register('email')}
          />
          {errors.email && <p className="mt-1.5 text-xs text-danger">{errors.email.message}</p>}
        </div>

        <div>
          <label htmlFor="password" className="block text-sm font-medium">
            Password
          </label>
          <input
            id="password"
            type="password"
            autoComplete="current-password"
            className="auth-input mt-1.5"
            {...register('password')}
          />
          {errors.password && (
            <p className="mt-1.5 text-xs text-danger">{errors.password.message}</p>
          )}
        </div>

        <div className="flex items-center justify-between">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" className="auth-checkbox" {...register('rememberMe')} />
            Keep me signed in
          </label>
          <Link to="/reset-password" className="text-sm text-primary hover:underline">
            Forgot password?
          </Link>
        </div>

        {formError && (
          <p className="rounded bg-danger/10 px-3 py-2 text-xs text-danger" role="alert">
            {formError}
          </p>
        )}

        <button type="submit" disabled={isSubmitting} className="auth-btn">
          {isSubmitting ? 'Signing in…' : 'Sign in'}
        </button>
      </form>

      <p className="mt-6 text-center text-sm text-muted-foreground">
        New to Fleet Iris?{' '}
        <Link to="/signup" className="font-medium text-primary hover:underline">
          Create a workspace
        </Link>
      </p>
    </AuthShell>
  );
}
